/**
 * NewsPagination — paginação numerada própria (sem semantic-ui), usada pela
 * variação `newsCenter` do Listing via `ListingBody` sombreado (ver
 * customizations/.../Listing/ListingBody.jsx).
 *
 * Mockup (Proposta Layout 03-web.png / 02-mobile-3.png): "1 2 3 4 5 … 11 ›"
 * — número ativo em círculo dourado cheio, os demais em outline, `…` quando
 * há salto, seta `›` circular dourada só para "próxima página" (não há
 * botão "anterior" no mockup).
 */
import { defineMessages, useIntl } from 'react-intl';
import cx from 'clsx';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import arrowPaginationSVG from '../../icons/arrow-pagination.svg';

const messages = defineMessages({
  label: {
    id: 'newsPagination.label',
    defaultMessage: 'News pagination',
  },
  page: {
    id: 'newsPagination.page',
    defaultMessage: 'Page {page}',
  },
  next: {
    id: 'newsPagination.next',
    defaultMessage: 'Next page',
  },
});

export type NewsPaginationProps = {
  activePage: number;
  totalPages: number;
  prevBatch?: unknown;
  nextBatch?: unknown;
  onPageChange: (page: number) => void;
};

const ELLIPSIS = 'ellipsis' as const;

// Janela ao redor da página atual + âncoras de primeira/última página, com
// `…` nos saltos — mesma lógica de qualquer paginação "1 2 3 … N" comum.
const getRange = (current: number, total: number, siblingCount = 2) => {
  if (total <= siblingCount * 2 + 3) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const start = Math.max(2, current - siblingCount);
  const end = Math.min(total - 1, current + siblingCount);
  const range: (number | typeof ELLIPSIS)[] = [1];
  if (start > 2) range.push(ELLIPSIS);
  for (let page = start; page <= end; page += 1) range.push(page);
  if (end < total - 1) range.push(ELLIPSIS);
  range.push(total);
  return range;
};

const NewsPagination = ({
  activePage,
  totalPages,
  nextBatch,
  onPageChange,
}: NewsPaginationProps) => {
  const intl = useIntl();
  const range = getRange(activePage, totalPages);

  return (
    <nav
      className="news-pagination"
      aria-label={intl.formatMessage(messages.label)}
    >
      <ol className="news-pagination__list">
        {range.map((page, i) =>
          page === ELLIPSIS ? (
            // eslint-disable-next-line react/no-array-index-key
            <li
              key={`ellipsis-${i}`}
              className="news-pagination__ellipsis"
              aria-hidden="true"
            >
              …
            </li>
          ) : (
            <li key={page}>
              <button
                type="button"
                className={cx('news-pagination__item', {
                  'news-pagination__item--active': page === activePage,
                })}
                aria-current={page === activePage ? 'page' : undefined}
                aria-label={intl.formatMessage(messages.page, { page })}
                onClick={() => page !== activePage && onPageChange(page)}
              >
                {page}
              </button>
            </li>
          ),
        )}
        <li>
          <button
            type="button"
            className="news-pagination__next"
            disabled={!nextBatch}
            aria-label={intl.formatMessage(messages.next)}
            onClick={() => onPageChange(activePage + 1)}
          >
            <Icon name={arrowPaginationSVG} size="18px" />
          </button>
        </li>
      </ol>
    </nav>
  );
};

export default NewsPagination;
